import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Button } from "@/shared/components/ui/button";
import { useDocuments, usePublishDocument } from "./use-documents";
import type { TermsOfService } from "../shared/compliance-types";
import { resolveComplianceError } from "../shared/resolve-compliance-error";

interface Props {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly doc: TermsOfService;
}

export function PublishDocumentDialog({ open, onOpenChange, doc }: Props) {
  const { t } = useTranslation();
  const publish = usePublishDocument();
  const [error, setError] = useState<string | null>(null);

  // Look up the currently-active predecessor in the same lineage so the
  // confirm copy names exactly what gets superseded. Falls back to the
  // "first publish" copy when none is found.
  const docsForLineage = useDocuments({
    roleScope: doc.roleScope,
    documentTypeId: doc.documentTypeId,
  });
  // A "live" predecessor in the same lineage is anything in ACTIVE or
  // SCHEDULED_RETIRE — the publish atomically retires it either way.
  const predecessor = docsForLineage.data?.find(
    (d) => d.id !== doc.id && (d.state === "ACTIVE" || d.state === "SCHEDULED_RETIRE"),
  );
  const isFirstPublish = predecessor == null;

  useEffect(() => {
    if (open) setError(null);
  }, [open]);

  const handleSubmit = async () => {
    if (publish.isPending) return;
    setError(null);
    try {
      await publish.mutateAsync(doc.id);
      onOpenChange(false);
    } catch (err) {
      setError(resolveComplianceError(err, t));
    }
  };

  const copyKey = isFirstPublish
    ? "compliance.documents.publish.firstPublish"
    : "compliance.documents.publish.supersede";

  return (
    <Dialog open={open} onOpenChange={(next) => !publish.isPending && onOpenChange(next)}>
      <DialogContent data-testid="compliance-publish-document-dialog">
        <DialogHeader>
          <DialogTitle>{t(`${copyKey}.title`)}</DialogTitle>
          <DialogDescription>
            {isFirstPublish
              ? t("compliance.documents.publish.firstPublish.body", {
                  role: t(`compliance.roles.${doc.roleScope}`),
                  version: doc.version,
                })
              : t("compliance.documents.publish.supersede.body", {
                  role: t(`compliance.roles.${doc.roleScope}`),
                  version: doc.version,
                  prevVersion: predecessor?.version ?? "",
                  prevDate: predecessor ? new Date(predecessor.effectiveFrom).toLocaleString() : "",
                })}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <p data-testid="compliance-publish-document-error" className="text-sm text-destructive">
            {error}
          </p>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={publish.isPending}
          >
            {t("compliance.actions.cancel")}
          </Button>
          <Button
            data-testid="compliance-publish-document-submit"
            onClick={handleSubmit}
            disabled={publish.isPending}
          >
            {publish.isPending
              ? t("compliance.documents.publish.publishing")
              : t(`${copyKey}.confirm`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
